import { useState, useEffect, useMemo } from 'react';
import { Global, useTheme } from '@emotion/react';
import {
  DayPicker,
  DayPickerDefaultProps,
  DayModifiers,
  Matcher,
} from 'react-day-picker';
import { format } from 'date-fns/format';
import { isBefore } from 'date-fns/isBefore';
import { isAfter } from 'date-fns/isAfter';

import Close from '@engineering/icons/close';
import { CustomMonths, CustomMonthsContext } from './CustomMonths';
import type { DateRange } from '../types';

import {
  GlobalStyles,
  DayPickerWrapper,
  DayPickerHeader,
  DayPickerFooter,
  Arrow,
  StyledButton,
} from './DatePicker.styles';
import { IconButton } from '../../../../../Ui';

type Props = {
  from?: Date;
  to?: Date;
  dayCountText?: string;
  dateFormat: string;
  onDatesChange?: (dateRange: DateRange) => void;
  onClose?: () => void;
  onScrollToBottom?: () => void;
  scrollToSelectedMonth: boolean;
  size?: 'small';
  vertical: boolean;
};

const DatePicker = ({
  from,
  to,
  dayCountText,
  scrollToSelectedMonth = false,
  onDatesChange,
  onClose,
  locale,
  dateFormat,
  size,
  onScrollToBottom,
  vertical,
  ...props
}: Props & DayPickerDefaultProps) => {
  const theme = useTheme();

  useEffect(() => {
    if (!scrollToSelectedMonth) {
      return;
    }
    const selectedDay = document.querySelector('.rdp-day_selected');
    const selectedMonthElm =
      selectedDay?.parentElement?.parentElement?.parentElement?.parentElement
        ?.parentElement;
    if (selectedMonthElm) {
      selectedMonthElm.scrollIntoView({
        block: 'center',
      });
    }
  }, [scrollToSelectedMonth]);

  const [selectedFrom, setFrom] = useState(from || null);
  const [selectedTo, setTo] = useState(to || null);
  const [hoveredTo, setHoveredTo] = useState(undefined as undefined | Date);

  const isSelectingFirstDay = (date: Date) => {
    const isBeforeFirstDay = selectedFrom && isBefore(date, selectedFrom);
    const isRangeSelected = selectedFrom && selectedTo;
    return !selectedFrom || isBeforeFirstDay || isRangeSelected;
  };

  const handleOnDayClick = (date: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled) {
      return;
    }

    if (isSelectingFirstDay(date)) {
      setFrom(date);
      setTo(null);
      setHoveredTo(undefined);
    } else {
      setTo(date);
      setHoveredTo(date);
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange({ from: selectedFrom, to: date });
      }
    }
  };

  const getHighlightedDays = () => {
    const highlightRules: Matcher[] = [
      (day: Date) => {
        if (selectedFrom && selectedTo) {
          return isAfter(day, selectedFrom) && isBefore(day, selectedTo);
        }
        return false;
      },
    ];

    if (selectedFrom) {
      highlightRules.push(selectedFrom);
    }

    if (selectedFrom && hoveredTo) {
      highlightRules.push({ from: selectedFrom, to: hoveredTo });
    }

    if (selectedFrom && selectedTo) {
      highlightRules.push({ from: selectedFrom, to: selectedTo });
    }

    return highlightRules;
  };

  const getModifiers = () => {
    let modifiers: DayModifiers = {};
    if (selectedFrom) {
      modifiers = { ...modifiers, from: [selectedFrom] };
    }
    if (hoveredTo) {
      modifiers = { ...modifiers, to: hoveredTo };
    } else if (selectedTo) {
      modifiers = { ...modifiers, to: selectedTo };
    }
    return modifiers;
  };

  const handleDayMouseEnter = (day: Date) => {
    if ((from && isBefore(day, from)) || isSelectingFirstDay(day)) {
      return;
    }
    setHoveredTo(day);
  };

  const context = useMemo(
    () => ({ onScrollToBottom, vertical }),
    [onScrollToBottom, vertical],
  );

  return (
    <CustomMonthsContext.Provider value={context}>
      <Global styles={GlobalStyles({ theme })} />
      <DayPickerWrapper fixed={!!dayCountText}>
        {dayCountText && (
          <DayPickerHeader>
            {from
              ? format(from, dateFormat, {
                  locale,
                })
              : '---'}
            <Arrow />
            {to
              ? format(to, dateFormat, {
                  locale,
                })
              : '---'}
            <IconButton
              icon={Close}
              title="close" // @TODO: Add translation
              onClick={onClose}
            />
          </DayPickerHeader>
        )}
        <DayPicker
          components={{ Months: CustomMonths }}
          disableNavigation={vertical}
          onDayClick={handleOnDayClick}
          onDayMouseEnter={handleDayMouseEnter}
          selected={getHighlightedDays()}
          modifiers={getModifiers()}
          disabled={{ before: new Date() }}
          locale={locale}
          modifiersClassNames={{
            outside: 'rdp-day_outside',
            disabled: 'rdp-day_disabled',
            today: 'rdp-day_today',
            from: 'rdp-day_range_start',
            to: 'rdp-day_range_end',
          }}
          {...props}
        />
        {dayCountText && (
          <DayPickerFooter>
            <StyledButton size={size} fullWidth onClick={onClose}>
              {dayCountText}
            </StyledButton>
          </DayPickerFooter>
        )}
      </DayPickerWrapper>
    </CustomMonthsContext.Provider>
  );
};

export { DatePicker };
